import { useCallback, useMemo, memo } from 'react';
import {
  ReactHookFormAutocomplete,
  Button,
  Grid,
  Popper,
  PopperActions,
  PopperContent,
  PopperTitle,
  Typography,
  useTranslations,
  usePubSub,
  buildFilterOptionsAsObject,
} from '@uniqkey-frontend/shared-app';
import { useForm } from 'react-hook-form';
import {
  EmployeeAccountStatus,
  EmployeeAccountType,
} from '@uniqkey-backend-partner/api-client';
import {
  IGetOrganizationEmployeeAccountsParams,
} from '../../../../../../hooks/useEmployeeAccountsAPI/interfaces';
import PubSubEventEnum from '../../../../../../enums/PubSubEventEnum';
import {
  EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS,
  EMPLOYEE_ACCOUNT_TYPES_TRANSLATION_KEYS,
} from '../../../../../../constants';

export interface IOrganizationEmployeesTabFilterSubmitResult {
  statusFilter: IGetOrganizationEmployeeAccountsParams['statusFilter'];
  typeFilter: IGetOrganizationEmployeeAccountsParams['typeFilter'];
}

interface IOrganizationEmployeesTabFilterProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onSubmit: (result: IOrganizationEmployeesTabFilterSubmitResult) => void;
  onClose: () => void;
  initialValues: IOrganizationEmployeesTabFilterSubmitResult;
}

type TStatusFilterOption = {
  label: string;
  value: IOrganizationEmployeesTabFilterSubmitResult['statusFilter'];
} | null;

type TTypeFilterOption = {
  label: string;
  value: IOrganizationEmployeesTabFilterSubmitResult['typeFilter'];
} | null;

interface IFormValues {
  statusFilter: TStatusFilterOption;
  typeFilter: TTypeFilterOption;
}

const POPPER_MIN_WIDTH = { minWidth: 500 };

const OrganizationEmployeesTabFilter = (props: IOrganizationEmployeesTabFilterProps) => {
  const {
    isOpen, anchorEl, onSubmit, onClose, initialValues,
  } = props;
  const { t } = useTranslations();

  const [statusFilterOptions, statusFilterOptionsAsObject] = useMemo(() => {
    const options = [
      {
        label: t(EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[EmployeeAccountStatus.Active]),
        value: EmployeeAccountStatus.Active,
      },
      {
        label: t(EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[EmployeeAccountStatus.Archived]),
        value: EmployeeAccountStatus.Archived,
      },
      {
        label: t(EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[EmployeeAccountStatus.Invited]),
        value: EmployeeAccountStatus.Invited,
      },
      {
        label: t(EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[EmployeeAccountStatus.Unprocessed]),
        value: EmployeeAccountStatus.Unprocessed,
      },
      {
        label: t(
          EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[EmployeeAccountStatus.ExistingUnprocessed],
        ),
        value: EmployeeAccountStatus.ExistingUnprocessed,
      },
      {
        label: t(EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[EmployeeAccountStatus.Staged]),
        value: EmployeeAccountStatus.Staged,
      },
    ];
    const optionsAsObject = buildFilterOptionsAsObject(options);
    return [options, optionsAsObject];
  }, [t]);

  const [typeFilterOptions, typeFilterOptionsAsObject] = useMemo(() => {
    const options = [
      {
        label: t(EMPLOYEE_ACCOUNT_TYPES_TRANSLATION_KEYS[EmployeeAccountType.Admin]),
        value: EmployeeAccountType.Admin,
      },
      {
        label: t(EMPLOYEE_ACCOUNT_TYPES_TRANSLATION_KEYS[EmployeeAccountType.Employee]),
        value: EmployeeAccountType.Employee,
      },
    ];
    const optionsAsObject = buildFilterOptionsAsObject(options);
    return [options, optionsAsObject];
  }, [t]);

  const {
    handleSubmit, control, reset, setValue, formState: { isDirty },
  } = useForm<IFormValues>({
    defaultValues: {
      statusFilter: statusFilterOptionsAsObject[
        initialValues.statusFilter as keyof typeof statusFilterOptionsAsObject
      ] ?? null,
      typeFilter: typeFilterOptionsAsObject[
        initialValues.typeFilter as keyof typeof typeFilterOptionsAsObject
      ] ?? null,
    },
  });

  const handleOnSubmit = useCallback((value: IFormValues): void => {
    const { statusFilter, typeFilter } = value;
    onSubmit({
      statusFilter: statusFilter?.value,
      typeFilter: typeFilter?.value,
    });
    onClose();
    reset(value); // set default values to the selected ones to reset isDirty
  }, [reset, onSubmit, onClose]);

  const clearAll = useCallback(() => {
    setValue('statusFilter', null, { shouldDirty: true });
    setValue('typeFilter', null, { shouldDirty: true });
  }, [setValue]);

  const handleOnFadeExited = useCallback(() => {
    reset(); // reset form values when Popper is closed and animation has finished
  }, [reset]);

  const handleReset = useCallback(() => {
    reset({
      statusFilter: null,
      typeFilter: null,
    });
  }, [reset]);
  usePubSub(PubSubEventEnum.RESET_FILTER, handleReset);

  return (
    <Popper
      anchorEl={anchorEl}
      offsetX={-8}
      offsetY={16}
      placement="bottom-start"
      open={isOpen}
      sx={POPPER_MIN_WIDTH}
      onFadeExited={handleOnFadeExited}
    >
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <PopperTitle onClose={onClose}>
          {t('common.filterBy')}
        </PopperTitle>
        <PopperContent>
          <Grid container direction="column" rowSpacing={2}>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('organizationEmployeesTabFilter.employeeStatus.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  name="statusFilter"
                  options={statusFilterOptions}
                  placeholder={t('organizationEmployeesTabFilter.employeeStatus.placeholder')}
                  control={control}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('organizationEmployeesTabFilter.adminRights.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  name="typeFilter"
                  options={typeFilterOptions}
                  placeholder={t('organizationEmployeesTabFilter.adminRights.placeholder')}
                  control={control}
                />
              </Grid>
            </Grid>
          </Grid>
        </PopperContent>
        <PopperActions>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button disabled={!isDirty} fullWidth type="submit">{t('common.apply')}</Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearAll}
              >
                {t('common.clearAll')}
              </Button>
            </Grid>
          </Grid>
        </PopperActions>
      </form>
    </Popper>
  );
};

export default memo(OrganizationEmployeesTabFilter);
